import React, { useEffect, useState, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { apiGet, apiPost, apiDelete } from "../../utils/api";
import { socket } from "../../socket";
import { useModal } from "../../context/ModalContext";
import {
  Card, CardHeader, CardBody, PageHeader, Spinner, Button
} from "../../components/ui";
import { FaWhatsapp, FaQrcode, FaPaperPlane, FaTrashAlt, FaPlug, FaCheckCircle } from "react-icons/fa";

export default function AdminSession() {
  const modal = useModal();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [loginSuccess, setLoginSuccess] = useState(false);

  // Send message state
  const [sendTo, setSendTo] = useState("");
  const [sendMsg, setSendMsg] = useState("");
  const [sending, setSending] = useState(false);

  const sessionRef = useRef(null);

  const fetchSession = async () => {
    try {
      setLoading(true);
      const res = await apiGet("/api/admin/session");
      if (res?.success && res.session) {
        setSession(res.session);
        sessionRef.current = res.session.sessionId;
        setLoginSuccess(res.session.status === "connected");
      } else {
        setSession(null);
        sessionRef.current = null;
      }
    } catch (err) {
      console.error("Failed to load admin session", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSession();
  }, []);

  useEffect(() => {
    const onQr = ({ qr, sessionId }) => {
      if (sessionRef.current && sessionId === sessionRef.current) {
        setQrCode(qr);
        setLoginSuccess(false);
      }
    };
    const onConnected = ({ sessionId }) => {
      if (sessionRef.current && sessionId === sessionRef.current) {
        setQrCode(null);
        setLoginSuccess(true);
        setConnecting(false);
        fetchSession();
      }
    };
    const onDisconnected = ({ sessionId }) => {
      if (sessionRef.current && sessionId === sessionRef.current) {
        setLoginSuccess(false);
        setQrCode(null);
        fetchSession();
      }
    };
    const onQrExpired = ({ sessionId }) => {
      if (sessionRef.current && sessionId === sessionRef.current) {
        setQrCode(null);
        setConnecting(false);
        modal.alert("QR code expired. Please try again.", "warning");
      }
    };

    socket.on("qr", onQr);
    socket.on("connected", onConnected);
    socket.on("disconnected", onDisconnected);
    socket.on("qr_expired", onQrExpired);

    return () => {
      socket.off("qr", onQr);
      socket.off("connected", onConnected);
      socket.off("disconnected", onDisconnected);
      socket.off("qr_expired", onQrExpired);
    };
  }, []);

  const handleConnect = async () => {
    const number = phone.replace(/[^0-9]/g, "");
    if (!number) return modal.alert("Enter a valid phone number", "warning");
    try {
      setConnecting(true);
      setQrCode(null);
      setLoginSuccess(false);
      const res = await apiPost("/api/admin/session", { phone: number });
      if (res?.success) {
        sessionRef.current = res.sessionId || number;
        await fetchSession();
      }
    } catch (err) {
      console.error("Failed to init admin session", err);
      modal.alert("Failed to start admin session", "error");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    const confirmed = await modal.confirm("Remove admin session? This will disconnect WhatsApp.");
    if (!confirmed) return;
    try {
      await apiDelete("/api/admin/session");
      setSession(null);
      setQrCode(null);
      setLoginSuccess(false);
      sessionRef.current = null;
      modal.alert("Admin session removed", "success");
    } catch (err) {
      modal.alert("Failed to remove session", "error");
    }
  };

  const handleSend = async () => {
    const number = sendTo.replace(/[^0-9]/g, "");
    if (!number || !sendMsg.trim()) return modal.alert("Enter recipient and message", "warning");
    try {
      setSending(true);
      const res = await apiPost("/api/admin/session/send", { to: number, message: sendMsg });
      if (res?.success) {
        modal.alert("Message sent", "success");
        setSendMsg("");
      }
    } catch (err) {
      modal.alert(err?.response?.data?.message || "Send failed", "error");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Spinner size="lg" />
      </div>
    );
  }

  const isConnected = session?.status === "connected" || loginSuccess;

  return (
    <div>
      <PageHeader
        title="Admin Session"
        subtitle="System WhatsApp session for automations, notifications, and utility messages"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Session Status */}
        <Card>
          <CardHeader title="Session Status" />
          <CardBody>
            {session ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${isConnected ? "bg-green-500" : "bg-red-400"}`} />
                  <div>
                    <div className="text-sm font-medium text-ink-primary">
                      {session.sessionId}
                    </div>
                    <div className="text-xs text-ink-secondary">
                      Status: <span className={isConnected ? "text-green-600 font-medium" : "text-red-500 font-medium"}>
                        {isConnected ? "Connected" : session.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* QR Code display */}
                {qrCode && !isConnected && (
                  <div className="border border-surface-border rounded-lg p-4 bg-white text-center">
                    <p className="text-xs text-ink-secondary mb-3">Scan with WhatsApp</p>
                    <QRCodeCanvas
                      value={qrCode}
                      size={200}
                      level="M"
                      marginSize={2}
                      title="Scan this QR code with WhatsApp"
                      className="mx-auto"
                    />
                  </div>
                )}

                {isConnected && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200">
                    <FaCheckCircle className="text-green-600" size={14} />
                    <span className="text-sm text-green-700 font-medium">Admin session is active and ready</span>
                  </div>
                )}

                <div className="flex gap-2">
                  {!isConnected && (
                    <button
                      onClick={handleConnect}
                      disabled={connecting}
                      className="btn-primary flex items-center gap-2 text-sm"
                    >
                      {connecting ? <Spinner size="sm" /> : <FaPlug size={12} />}
                      {connecting ? "Connecting..." : "Reconnect"}
                    </button>
                  )}
                  <button
                    onClick={handleDisconnect}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 transition-colors"
                  >
                    <FaTrashAlt size={10} /> Remove Session
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-ink-secondary">
                  No admin session configured. Connect a WhatsApp number to use as the system session for sending automation events, disconnect notifications, and utility messages.
                </p>

                {qrCode && (
                  <div className="border border-surface-border rounded-lg p-4 bg-white text-center">
                    <p className="text-xs text-ink-secondary mb-3">Scan with WhatsApp</p>
                    <QRCodeCanvas
                      value={qrCode}
                      size={200}
                      level="M"
                      marginSize={2}
                      title="Scan this QR code with WhatsApp"
                      className="mx-auto"
                    />
                  </div>
                )}

                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <label className="block text-xs text-ink-secondary mb-1">Phone Number (with country code)</label>
                    <input
                      type="text"
                      placeholder="919876543210"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleConnect()}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-surface-border bg-surface-bg text-ink-primary focus:outline-none focus:ring-1 focus:ring-green-400"
                    />
                  </div>
                  <button
                    onClick={handleConnect}
                    disabled={connecting || !phone.trim()}
                    className="btn-primary flex items-center gap-2 text-sm whitespace-nowrap"
                  >
                    {connecting ? <Spinner size="sm" /> : <FaWhatsapp size={14} />}
                    {connecting ? "Connecting..." : "Connect"}
                  </button>
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Quick Send */}
        <Card>
          <CardHeader title="Quick Send" />
          <CardBody>
            <p className="text-xs text-ink-secondary mb-4">
              Send a message through the admin session to any WhatsApp number.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-ink-secondary mb-1">Recipient (with country code)</label>
                <input
                  type="text"
                  placeholder="919876543210"
                  value={sendTo}
                  onChange={(e) => setSendTo(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-surface-border bg-surface-bg text-ink-primary focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="block text-xs text-ink-secondary mb-1">Message</label>
                <textarea
                  placeholder="Type your message..."
                  value={sendMsg}
                  onChange={(e) => setSendMsg(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-surface-border bg-surface-bg text-ink-primary focus:outline-none focus:ring-1 focus:ring-blue-400 resize-none"
                />
              </div>
              <button
                onClick={handleSend}
                disabled={sending || !isConnected || !sendTo.trim() || !sendMsg.trim()}
                className="btn-primary flex items-center gap-2 text-sm w-full justify-center"
              >
                {sending ? <Spinner size="sm" /> : <FaPaperPlane size={12} />}
                {sending ? "Sending..." : "Send Message"}
              </button>
              {!isConnected && (
                <p className="text-xs text-red-500 text-center">Connect admin session first to send messages</p>
              )}
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Use Cases */}
      <Card className="mt-6">
        <CardBody>
          <h3 className="text-sm font-semibold text-ink-primary mb-2">What is the Admin Session for?</h3>
          <ul className="text-xs text-ink-secondary space-y-1.5 list-disc list-inside">
            <li>Sending disconnect notifications to users when their WhatsApp session goes offline</li>
            <li>Running platform-wide automation events (welcome messages, onboarding sequences)</li>
            <li>Sending utility messages (OTPs, alerts, system notifications) on behalf of the platform</li>
            <li>Testing message delivery without using a user's session</li>
          </ul>
        </CardBody>
      </Card>
    </div>
  );
}
